import { formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";

interface FilterToggleProps {
  /** What the toggle keeps: "Geblokkeerd". */
  label: string;
  /** How many items it would show (`toggleCounts`). */
  count: number;
  pressed: boolean;
  onPressedChange: (pressed: boolean) => void;
  /** Such as `w-full`, for a toggle that fills its place in a filter row. */
  className?: string;
}

/**
 * A filter that is on or off, as a button that stays pressed (`aria-pressed`)
 * while it filters: its label and its count in a small chip, as high as the
 * filter row's other controls. Pressed, it takes De Ceuster's light blue tint
 * with a blue border and a solid blue count. Shared by the filter rows of
 * the Roadmap, Statistieken and Teams.
 *
 *     <FilterToggle label="Alleen met fouten" count={withErrors} pressed={onlyErrors}
 *       onPressedChange={setOnlyErrors} className="w-full justify-between" />
 */
export function FilterToggle({
  label,
  count,
  pressed,
  onPressedChange,
  className,
}: FilterToggleProps) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={() => onPressedChange(!pressed)}
      className={cn(
        "ring-offset-background focus-visible:ring-ring inline-flex h-9 items-center gap-2 rounded-md border px-3 text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none pointer-coarse:h-10",
        pressed
          ? "border-primary bg-accent-light text-primary"
          : "border-input bg-card text-foreground hover:bg-accent-light hover:text-primary",
        className,
      )}
    >
      {label}
      <span
        className={cn(
          "rounded-sm px-1.5 text-xs leading-5 tabular-nums",
          pressed ? "bg-primary text-primary-foreground" : "bg-muted/15 text-muted-foreground",
        )}
      >
        {formatNumber(count)}
      </span>
    </button>
  );
}
